/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import Tag from '../../../domain/models/Tag';
import getTags from '../../../domain/use_cases/tags/getTags';
import createTag from '../../../domain/use_cases/tags/createTag';
import updateTag from '../../../domain/use_cases/tags/updateTag';
import deleteTag, { DeleteTagFailure } from '../../../domain/use_cases/tags/deleteTag';
import AuthenticationContext from '../auth/AuthenticationContext';
import { formatErrorMessage } from '../../../core/utils/errorUtils';

interface TagsContextProps {
  tags: Tag[];
  domainId: string | null;
  subscriptionId: string | null;
  status: ContextStatus;
  createStatus: ContextStatus;
  updateStatus: ContextStatus;
  deleteStatus: ContextStatus;
  errorMessage: string;
  fetch: (domainId: string | null, subscriptionId?: string | null) => Promise<void>;
  submitCreate: (tag: Tag) => Promise<void>;
  submitUpdate: (tag: Tag) => Promise<void>;
  submitDelete: (tagId: string) => Promise<void>;
  reset: () => void;
}

const TagsContext = createContext<TagsContextProps>({
  tags: [],
  domainId: null,
  subscriptionId: null,
  status: ContextStatus.IDLE,
  createStatus: ContextStatus.IDLE,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  errorMessage: '',
  fetch: async () => {},
  submitCreate: async () => {},
  submitUpdate: async () => {},
  submitDelete: async () => {},
  reset: () => {},
});

export const TagsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [domainId, setDomainId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [createStatus, setCreateStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [updateStatus, setUpdateStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const authContext = useContext(AuthenticationContext);

  const fetch = async (newDomainId: string | null, newSubscriptionId: string | null = null) => {
    setDomainId(newDomainId);
    setSubscriptionId(newSubscriptionId);
    setStatus(ContextStatus.LOADING);
    try {
      const fetchedTags = await getTags(newDomainId, newSubscriptionId);
      setTags(fetchedTags);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('[TagsContext] fetch failed:', error);
      setTags([]);
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitCreate = async (tag: Tag) => {
    if (authContext.authentication === null) return;
    setCreateStatus(ContextStatus.LOADING);
    try {
      await createTag(tag, authContext.authentication);
      setCreateStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('[TagsContext] create failed:', error);
      setCreateStatus(ContextStatus.ERROR);
    }
  };

  const submitUpdate = async (tag: Tag) => {
    if (authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updateTag(tag, authContext.authentication);
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('[TagsContext] update failed:', error);
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async (tagId: string) => {
    if (authContext.authentication === null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteTag(tagId, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof DeleteTagFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setTags([]);
    setDomainId(null);
    setSubscriptionId(null);
    setStatus(ContextStatus.IDLE);
    setCreateStatus(ContextStatus.IDLE);
    setUpdateStatus(ContextStatus.IDLE);
    setDeleteStatus(ContextStatus.IDLE);
    setErrorMessage('');
  };

  return (
    <TagsContext.Provider
      value={{
        tags,
        domainId,
        subscriptionId,
        status,
        createStatus,
        updateStatus,
        deleteStatus,
        errorMessage,
        fetch,
        submitCreate,
        submitUpdate,
        submitDelete,
        reset,
      }}
    >
      {children}
    </TagsContext.Provider>
  );
};

export default TagsContext;
